import { SearchCollection, SearchResult } from "./faq-search-types";

type SearchBackTrack = {
  ranges: Range[];
  currentPosition: number;
  remainingCharacters: number;
};

type NumberRange = {
  start: number;
  end: number;
};

/**
 * Gets all matches of a given search term in a string
 * @param searchTerm The term to search for
 * @param text The string to search in
 * @param isCaseSensitive True if case-sensitive
 * @returns An array of positions
 */
const getMatchesInString = (
  searchTerm: string,
  text: string,
  isCaseSensitive = false
) => {
  const matches = [];

  const usedTerm = isCaseSensitive ? searchTerm : searchTerm.toLowerCase();
  const usedText = isCaseSensitive ? text : text.toLowerCase();

  let startPos = 0;
  while (startPos < usedText.length) {
    const index = usedText.indexOf(usedTerm, startPos);
    if (index === -1) break;
    startPos = index + usedTerm.length;
    matches.push(index);
  }

  return matches;
};

/**
 * Search a collection for a string
 * @param collection The collection to search through
 * @param searchTerm The term to search for
 */
export const searchCollection = (
  collection: SearchCollection[],
  searchTerm: string
): SearchResult[] => {
  const results: SearchResult[] = [];

  for (const entry of collection) {
    const matches = getMatchesInString(searchTerm, entry.text);
    let matchIndex = 0;

    // Find search term in text nodes
    const entryResults = entry.tokens
      .map((element) => ({ element, text: element.textContent || "" }))
      .reduce<SearchBackTrack>(
        (result, { element, text }) => {
          const indices: NumberRange[] = [];
          let nodeOffset = 0;

          // if there are no more matches, return all the way
          if (matchIndex >= matches.length || text.length === 0) {
            return result;
          }

          let relativePosition = matches[matchIndex] - result.currentPosition;

          // if there are remaining characters that didn't get wrapped into highlighting
          if (result.remainingCharacters > 0) {
            nodeOffset = Math.min(result.remainingCharacters, text.length);

            indices.push({ start: 0, end: nodeOffset });
            // eslint-disable-next-line no-param-reassign
            result.remainingCharacters -= nodeOffset;

            // get next match if match does not continue in new node
            if (nodeOffset <= text.length && result.remainingCharacters === 0) {
              matchIndex += 1;
              if (matchIndex > matches.length) {
                return result;
              }
            }

            relativePosition = matches[matchIndex] - result.currentPosition;
          }

          // for all matches inside the node
          while (relativePosition < text.length && relativePosition >= 0) {
            // if the result reaches out of the node, wrap it and set the remainder
            if (relativePosition + searchTerm.length > text.length) {
              // eslint-disable-next-line no-param-reassign
              result.remainingCharacters =
                relativePosition + searchTerm.length - text.length;
              indices.push({
                start: relativePosition,
                end:
                  relativePosition +
                  searchTerm.length -
                  result.remainingCharacters,
              });
              break;
            }

            indices.push({
              start: relativePosition,
              end: relativePosition + searchTerm.length,
            });

            // if there are no more matches, return all the way
            matchIndex += 1;
            if (matchIndex > matches.length) {
              return result;
            }

            relativePosition = matches[matchIndex] - result.currentPosition;
          }

          // Create a range object for each instance of search term we found in the text node
          const ranges = indices.map((index) => {
            const range = new Range();
            range.setStart(element, index.start);
            range.setEnd(element, index.end);
            return range;
          });

          // eslint-disable-next-line no-param-reassign
          result.currentPosition += text.length;

          if (ranges.length === 0) {
            return result;
          }

          result.ranges.push(...ranges);
          return result;
        },
        {
          ranges: [],
          currentPosition: 0,
          remainingCharacters: 0,
        }
      );

    if (entryResults.ranges.length === 0) {
      continue;
    }

    results.push({
      id: entry.id,
      ranges: entryResults.ranges,
      element: entry.element,
    });
  }

  return results;
};
